import type { DataModelView } from '#core/lib/cognite/view.ts';

import { createReshapedView, type VirtualView } from '../createReshapedView.ts';
import {
  IDENTITY,
  OPPORTUNITY_RECORD,
  REALIZED_VALUE_GAINS,
  type Shape,
  STRING_RELATION,
  VALUE_ESTIMATES,
  VALUE_TRACKING_CONTEXT,
} from '../shapes/index.ts';
import { useCatalogEnrichContext } from './useCatalogEnrichContext.ts';
import type { ViewFamilySpec } from './viewFamilySpec.ts';
import { OpportunityAgentView, ValueTrackingAgentView } from '#schema/generated/ipso/views.ts';

/**
 * Identity + the producing-string direct-relation kept unrenamed on every
 * record view. `wellString` is the WELL grain ref the agent scopes by /
 * joins the well-string entity view against — kept verbatim, never name-enriched.
 */
const WELL_KEEP: Shape = [...IDENTITY, ...STRING_RELATION];

/** One entity-record surface: its registry name, source view, agent doc, and the measure groups it projects. */
type RecordSpec = ViewFamilySpec<readonly Shape[]> & {
  readonly source: DataModelView;
  readonly groups: readonly Shape[];
};

/**
 * The two entity-record surfaces, declared as data. Both front a single
 * flat source over the WELL grain, keeping identity + `wellString`. These are
 * de-prefix pass-throughs (the sources carry no prefix; `suffix === canonical`),
 * mostly SCALAR record columns — NOT timeseries-heavy like the rollup views.
 *
 * `ValueTracking` collapses two interfaces (`ValueEstimates` /
 * `RealizedValueGains`) into one view: there are NO separate sources for them —
 * both are sub-groups of the single `ValueTracking_Agent` source, composed here
 * as `VALUE_ESTIMATES` (the estimate scalars) + `REALIZED_VALUE_GAINS` (the
 * realized timeseries) alongside the shared `VALUE_TRACKING_CONTEXT` scalars.
 */
const RECORD_SPECS: readonly RecordSpec[] = [
  {
    name: 'Opportunity',
    description:
      'Production-improvement opportunities (WELL grain): each row is one opportunity with its classification (type/status/source/owner), description, lifecycle dates, baseline vs estimated oil/gas/water rates, cumulative realized oil gain, and validation + value-tracking outcome. Filter by wellString (or join the well-string entity) to scope to a well string.',
    source: OpportunityAgentView,
    groups: [OPPORTUNITY_RECORD],
  },
  {
    name: 'ValueTracking',
    description:
      'Optimization value tracking (WELL grain): each row tracks the realized value of an optimization over a time window — the optimized control parameter + scenario, estimated post-optimization rates and target gains (the value-estimate columns), and the realized rate/volume/dollar gains and well uptime as timeseries (the realized-value-gain columns). Filter by wellString (or join the well-string entity) to scope to a well string.',
    source: ValueTrackingAgentView,
    groups: [VALUE_TRACKING_CONTEXT, VALUE_ESTIMATES, REALIZED_VALUE_GAINS],
  },
];

/**
 * Build the two de-prefixed entity-record surfaces fronting
 * `Opportunity_Agent` and `ValueTracking_Agent`. Pure reshape views — no `deps`
 * needed; each projects its record measures over the well grain, keeping
 * identity + the `wellString` relation.
 */
export const useRecordsViews = (): readonly VirtualView[] => {
  const { client, mintSeries } = useCatalogEnrichContext();
  return RECORD_SPECS.map((spec: RecordSpec) =>
    createReshapedView({
      name: spec.name,
      description: spec.description,
      source: spec.source,
      keep: WELL_KEEP,
      groups: spec.groups.map((shape: Shape) => ({ shape })),
      client,
      mintSeries,
    }),
  );
};
